import { useCallback, useContext } from 'react'
import { useDispatch } from 'react-redux'

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { FeedbackContext } from '~/components/feedback'
import { formatDateToRequest } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import {
  linkCampaignCheckingAccountService,
  unlinkCampaignCheckingAccountService
} from '~/modules/retailMedia/services/campaign/checking-accounts'
import { getCampaign } from '~/modules/retailMedia/store/campaign/actions'
import { useAppSelector } from '~/store/hooks'

const prefix = 'rm:campaign.detail.insertionOrder.toast'

/**
 * Link / unlink a PI wallet. The section list comes from the campaign detail,
 * so success reloads it in the background (no local additions or removals).
 */
export const useCampaignInsertionOrderMutations = (campaignId: string) => {
  const { t } = useAPPTranslation()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { toast } = useContext(FeedbackContext)

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  // A failed reload keeps the page (the link/unlink already happened), and a
  // late response is dropped if the store moved to another campaign.
  const refresh = useCallback(() => {
    dispatch(
      getCampaign({
        start_date: formatDateToRequest(startDate),
        end_date: formatDateToRequest(endDate),
        campaign_id: campaignId,
        account_info: true,
        requestInBackground: true,
        preserveOnError: true,
        expectedCampaignId: campaignId
      })
    )
    queryClient.invalidateQueries({ queryKey: ['audit-log', campaignId] })
  }, [campaignId, dispatch, endDate, queryClient, startDate])

  const link = useMutation({
    mutationFn: (checkingAccountId: string) =>
      linkCampaignCheckingAccountService({
        campaignId,
        body: { checking_account_id: checkingAccountId }
      }),
    onSuccess: () => {
      toast.success(t(`${prefix}.linkSuccess`))
      refresh()
    },
    onError: () => {
      toast.critical(t(`${prefix}.linkError`))
    }
  })

  const unlink = useMutation({
    mutationFn: (campaignCheckingAccountId: string) =>
      unlinkCampaignCheckingAccountService({
        campaignId,
        campaignCheckingAccountId
      }),
    onSuccess: () => {
      toast.success(t(`${prefix}.unlinkSuccess`))
      refresh()
    },
    onError: () => {
      toast.critical(t(`${prefix}.unlinkError`))
    }
  })

  return {
    link,
    unlink,
    isPending: link.isPending || unlink.isPending
  }
}
